import { mergeAttributes, Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from "@tiptap/react";
import { ImageOff } from "lucide-react";
import { useAttachmentUrl } from "@/lib/attachments";

/**
 * An image stored as an attachment ID rather than a URL.
 *
 * R2 presigned URLs expire after an hour, so persisting one into the document
 * would leave a permanently broken image. The node stores the id and resolves a
 * fresh URL on render.
 */
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    sebasImage: {
      insertSebasImage: (attrs: { attachmentId: string; alt?: string }) => ReturnType;
    };
  }
}

function ImageView({ node }: NodeViewProps) {
  const attachmentId = node.attrs.attachmentId as string;
  const alt = (node.attrs.alt as string) ?? "";
  const { url, error } = useAttachmentUrl(attachmentId);

  return (
    <NodeViewWrapper className="my-4">
      {error ? (
        <div className="flex items-center gap-2 rounded-2xl bg-sunken px-4 py-6 text-sm text-ink-muted">
          <ImageOff className="h-4 w-4" /> Image unavailable
        </div>
      ) : url ? (
        // `alt` carries the filename for screen readers and for search indexing,
        // but is not rendered as a caption — a filename is not a caption.
        <img src={url} alt={alt} className="max-w-full rounded-2xl" />
      ) : (
        <div className="h-48 w-full animate-pulse rounded-2xl bg-sunken" />
      )}
    </NodeViewWrapper>
  );
}

export const SebasImage = Node.create({
  name: "image",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes: () => ({
    attachmentId: { default: null },
    alt: { default: "" },
  }),

  parseHTML: () => [{ tag: 'div[data-sebas-image]' }],

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-sebas-image": "" })];
  },

  addNodeView: () => ReactNodeViewRenderer(ImageView),

  addCommands() {
    return {
      insertSebasImage:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: "image", attrs }),
    };
  },
});
